import { Button } from '@progress/kendo-react-buttons';
import { Switch } from '@progress/kendo-react-inputs';
import { Card, CardBody } from '@progress/kendo-react-layout';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import { MODEL_SEPARATOR } from '../constants';
import { SearchBox } from '../components';
import { useDebounce } from 'app/hooks/useDebounce';
import { getDefaultThumbnail } from 'app/pages/Equipment/utils';
import ClickOutside from 'app/widgets/ClickOutside';
import {
  ic_nav_back as BackIcon,
  ic_filter as FilterIcon,
} from 'app/widgets/SVGs';
import ChevronRightIcon from 'assets/images/chevron_right.svg';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  selectCategoryBySlugPath,
  selectCategoryCounts,
  selectCompatibleIdsByMachineId,
  selectFilteredModels,
  selectModelById,
  selectModelsBySearchQueryAndCategory,
  selectSubCategoriesByParentId,
  selectVisibleCategoryIds,
} from 'services/api/resources/selectors';
import { ResourceFilters } from 'services/api/resources/types';
import { useAppSelector } from 'services/store';
import './CategoriesPage.less';
import { ResourceChipCrumbs, Chip } from './components';

interface Props {
  categoryPath: string;
}

export default function CategoriesPage({ categoryPath }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [excludeDiscontinuedModels, setExcludeDiscontinuedModels] =
    useState(false);

  const filterMachineId = searchParams.get('compatibleWith');

  const model = useAppSelector(state =>
    selectModelById(state, filterMachineId ?? ''),
  );

  const compatibleIds = useAppSelector(state =>
    selectCompatibleIdsByMachineId(state, filterMachineId),
  );

  const filters: ResourceFilters = useMemo(
    () => ({
      compatibleIds,
      excludeDiscontinued: excludeDiscontinuedModels,
    }),
    [compatibleIds, excludeDiscontinuedModels],
  );

  const categoryCounts = useAppSelector(state =>
    selectCategoryCounts(state, filters),
  );

  const visibleCategorySet = useAppSelector(state =>
    selectVisibleCategoryIds(state, filters),
  );

  const pathSlugs = categoryPath ? categoryPath.split('/').filter(Boolean) : [];

  const category = useAppSelector(state =>
    selectCategoryBySlugPath(state, pathSlugs),
  );

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const searchResults = useAppSelector(state =>
    selectModelsBySearchQueryAndCategory(
      state,
      debouncedSearchTerm,
      category?.id,
      filters,
    ),
  );

  const allSubCategories = useAppSelector(state =>
    selectSubCategoriesByParentId(state, Number(category?.id)),
  );

  const visibleSubCategories = useMemo(() => {
    if (!visibleCategorySet) return allSubCategories;
    return allSubCategories.filter(sub => visibleCategorySet.has(sub.id));
  }, [allSubCategories, visibleCategorySet]);

  const models = useAppSelector(state =>
    selectFilteredModels(state, Number(category?.id), filters),
  );

  const isAttachmentType = model?.type === 'attachment';

  const title = filterMachineId
    ? t(isAttachmentType ? 'compatible_machines' : 'compatible_attachments')
    : category?.name;

  const isLeafCategory = visibleSubCategories.length === 0;

  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);
  useEffect(() => {
    let index: number = setClassNames('layout-grey');
    return () => removeClassNames(index);
  }, []);

  useEffect(() => {
    if (visibleSubCategories.length === 1) {
      const singleCategory = visibleSubCategories[0];

      console.log(
        `Auto-redirecting to single category: ${singleCategory.name}`,
      );

      navigate(
        {
          pathname: `/resources/${categoryPath}/${visibleSubCategories[0].slug}`,
          search: searchParams.toString(),
        },
        { replace: true },
      );
    }
  }, [visibleSubCategories, navigate, searchParams]);

  const buildLink = (subPath: string) => {
    return {
      pathname: `/resources${categoryPath ? `/${categoryPath}` : ''}${subPath}`,
      search: location.search,
    };
  };

  const handleFilterChange = event => {
    setExcludeDiscontinuedModels(event.target.value);
    setIsFilterExpanded(false);
  };

  if (!category)
    return (
      <div className="text-center">
        {t('resources.errors.category_not_found')}
      </div>
    );

  return (
    <div className="relative">
      <span
        className="back-button back-button-on-layout fixed-on-desktop"
        onClick={() => navigate(-1)}
      >
        <BackIcon />
      </span>
      <div className="categories-page__titles-block">
        <div className="title-with-search and-filter categories-page__title-row">
          <div className="title-box">
            <h1 className="categories-page__title">{title}</h1>
          </div>

          <SearchBox
            searchTerm={searchTerm}
            searchFor={category.name}
            searchResults={searchResults}
            setSearchTerm={setSearchTerm}
          />

          <div className="filter-box">
            <ClickOutside
              className="filter"
              onClickOutside={() => {
                setIsFilterExpanded(false);
              }}
            >
              <Button
                className="button-filter"
                onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              >
                <FilterIcon />
                {t('equipment_tree_btn_filter')}
              </Button>

              <div className="filter-options">
                {isFilterExpanded && (
                  <Card>
                    <CardBody>
                      <div>{t('equipment_tree_filter_discontinued')}</div>
                      <Switch
                        checked={excludeDiscontinuedModels}
                        onChange={handleFilterChange}
                        onLabel=""
                        offLabel=""
                      />
                    </CardBody>
                  </Card>
                )}
              </div>
            </ClickOutside>
          </div>
        </div>

        {!!filterMachineId && (
          <h2 className="categories-page__subtitle">
            {t('model_compatible_with', { s: filterMachineId })}
          </h2>
        )}
      </div>

      <div className="categories-page__chips-crumbs">
        <ResourceChipCrumbs categoryPath={categoryPath || ''} />

        {excludeDiscontinuedModels && (
          <Chip
            label={t('equipment_tree_filter_discontinued')}
            onClick={() => setExcludeDiscontinuedModels(false)}
          />
        )}
      </div>

      {!isLeafCategory &&
        (!!visibleSubCategories.length ? (
          <div className="add-equipment-boxes resources-style">
            {visibleSubCategories.map(cat => {
              const count = categoryCounts[cat.id] || 0;
              return (
                <span key={cat.id}>
                  <div className="equipment-content-actions section">
                    <div
                      className="add-equipment-item"
                      onClick={() => {
                        navigate(buildLink(`/${cat.slug}`));
                      }}
                    >
                      <div className="product-length">
                        <span>{count}</span>
                      </div>

                      <div className="add-equipment-box-img">
                        <img
                          src={
                            cat.iconUrl ||
                            getDefaultThumbnail(
                              category
                                ? category.name === 'Attachments' ||
                                  category.name === 'Accessoires'
                                  ? 'attachment'
                                  : category.name
                                : 'attachment',
                              category.name,
                            )
                          }
                          onError={({ currentTarget }) => {
                            currentTarget.onerror = null;
                            currentTarget.src = getDefaultThumbnail(
                              category
                                ? category.name === 'Attachments' ||
                                  category.name === 'Accessoires'
                                  ? 'attachment'
                                  : category.name
                                : 'attachment',
                              category.name,
                            );
                          }}
                          alt="Category"
                        />
                      </div>

                      <h3>{cat.name}</h3>
                      <img
                        className="arrow-links_icon arrow-links_icon-right"
                        src={ChevronRightIcon}
                      />
                    </div>
                  </div>
                </span>
              );
            })}
          </div>
        ) : (
          <div className="text-center">
            {t('resources.errors.no_compatible_models_found')}
          </div>
        ))}

      {isLeafCategory &&
        (!!models.length ? (
          <div className="add-equipment-boxes resources-style">
            {models.map(model => (
              <div
                className="equipment-content-actions section"
                key={model.model}
              >
                <div
                  className="add-equipment-item"
                  onClick={() =>
                    navigate(buildLink(`${MODEL_SEPARATOR}${model.model}`))
                  }
                >
                  <div className="add-equipment-box-img">
                    <img
                      src={
                        model.modelFullUrl ||
                        model.modelIconUrl ||
                        getDefaultThumbnail(
                          model.type || 'attachment',
                          category.name,
                        )
                      }
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = getDefaultThumbnail(
                          model.type || 'attachment',
                          category.name,
                        );
                      }}
                      alt="Model"
                    />
                  </div>
                  <div className="item-grow">
                    <h3>{model.model}</h3>
                    <span>{model.modelDescription}</span>
                  </div>
                  <img
                    className="arrow-links_icon arrow-links_icon-right"
                    src={ChevronRightIcon}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center">
            {t('resources.errors.no_compatible_models_found')}
          </div>
        ))}
    </div>
  );
}
